import { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { FilterContentV2Pill } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2Pill';
import { FilterContentV2ValueDropdown } from '@/comps/FilterPicker/filter-content-v2/FilterContentV2ValueDropdown';
import { buildGenericFilter, FilterOperator, getOperatorLabel } from '@/comps/FilterPicker/filter-operators';
import { buildFilterKindName, formatValue, getVariable, useGetFilterValues } from '@/comps/FilterPicker/utils';
import { AvailableValue, GenericFilter, VolumeKind } from '@/comps/FilterPicker/FilterPicker';
import { QueryOrdering, TimeRangeConfig } from '@/api/generated/model';
import { CheckedState } from '@radix-ui/react-checkbox';
import { useDebounceValue } from '@/hooks/use-debounce';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { useAliasedKindName } from '@/contexts/alias';
import { useFilterSelectAllV2 } from '@/comps/FilterPicker/filter-content-v2/useFilterSelectAllV2';
import { FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS } from '@/comps/FilterPicker/filter-content-v2/constants';
import { List, ListItem } from '@/comps/list';
import { Button } from '@/comps/primitives/button';
import { cn } from '@/utils/shad';
import { humanizeString } from '@/utils/string.utils';
import { translate } from '@/utils/translations';

type DraftPopover = 'field' | 'value' | null;

const PILL_LABEL_CLASS_NAMES = 'text-xs font-semibold text-white';
const PILL_CHEVRON_CLASS_NAMES = 'text-white';
const NESTED_POPOVER_CONTENT_CLASS = FILTER_CONTENT_V2_NESTED_POPOVER_CONTENT_CLASS;

export type FilterContentV2NestedFilterRowProps = {
  filter: GenericFilter | null;
  onChange: (filter: GenericFilter | null) => void;
  fieldKinds: string[];
  projectId: string;
  timeRange: TimeRangeConfig;
  volumeKind?: VolumeKind;
  operator?: FilterOperator;
  showRemove?: boolean;
  onRemove?: () => void;
  openValueOnFieldSelect?: boolean;
  onValueApplied?: () => void;
  className?: string;
};

function formatValuesForPillDisplay(fieldKind: string, values: string[]): string {
  return values
    .map((value) => {
      const formatted = formatValue(fieldKind, value);
      return typeof formatted === 'string' ? formatted : value;
    })
    .join(', ');
}

export function FilterContentV2NestedFilterRow({
  filter,
  onChange,
  fieldKinds,
  projectId,
  timeRange,
  volumeKind = 'users',
  operator = 'equals',
  showRemove = false,
  onRemove,
  openValueOnFieldSelect = true,
  onValueApplied,
  className,
}: FilterContentV2NestedFilterRowProps) {
  const [openPopover, setOpenPopover] = useState<DraftPopover>(null);
  const [filterKind, setFilterKind] = useState<string | null>(filter?.column ?? null);
  const [localFilter, setLocalFilter] = useState<GenericFilter | null>(filter);
  const [searchFilterValue, setSearchFilterValue] = useState('');
  const [sortBy, setSortBy] = useState<QueryOrdering>({ by: getVariable(), direction: 'desc' });
  const debouncedSearch = useDebounceValue(searchFilterValue);
  const { aggregateBy } = useGlobalFiltersContext();
  const aliasKindName = useAliasedKindName();

  useEffect(() => {
    setFilterKind(filter?.column ?? null);
    setLocalFilter(filter);
  }, [filter]);

  const targetFilterValues = useMemo(
    () => (Array.isArray(localFilter?.values) ? localFilter.values : []),
    [localFilter?.values],
  );

  const filtersForFetch = useMemo(() => (localFilter ? [localFilter] : []), [localFilter]);

  const filtersMapByKind = useMemo(() => {
    if (!localFilter?.column) return {};
    const values = Array.isArray(localFilter.values) ? localFilter.values : [];
    return { [localFilter.column]: values };
  }, [localFilter]);

  const { availableFilterValues: tempAvailableFilterValues, isLoading } = useGetFilterValues({
    filters: filtersForFetch,
    filterKind,
    timeRange,
    projectId,
    search: debouncedSearch,
    filtersMapByKind,
    isSelectedAll: localFilter?.select_all,
    volumeKind,
    sortBy,
    aggregateBy,
    enabled: !!filterKind && operator !== 'contains',
  });

  const showSelectAll = (tempAvailableFilterValues?.length ?? 0) > 1;

  const { handleOnCheckSelectAll, hasSelectedAll, allValuesSnapshot } = useFilterSelectAllV2({
    filters: filtersForFetch,
    filterKind,
    availableFilterValues: tempAvailableFilterValues?.map((a) => a.value) ?? [],
    setFilters: (nextFilters) => {
      const next = nextFilters[0] ?? null;
      setLocalFilter(next);
      onChange(next);
      if (next?.values?.length) onValueApplied?.();
    },
    isExclude: operator === 'is_not',
  });

  const commitFilterValues = useCallback(
    (values: string[], selectAllOverride?: boolean) => {
      if (!filterKind) return;

      let nextFilter: GenericFilter | null = null;
      if (values.length || selectAllOverride) {
        nextFilter = buildGenericFilter({
          column: filterKind,
          operator,
          values,
          selectAll: selectAllOverride ?? localFilter?.select_all,
        });
      }

      setLocalFilter(nextFilter);
      onChange(nextFilter);
      if (nextFilter?.values?.length || nextFilter?.select_all) {
        onValueApplied?.();
      }
    },
    [filterKind, localFilter?.select_all, onChange, onValueApplied, operator],
  );

  const handleSelectField = useCallback(
    (kind: string) => {
      setFilterKind(kind);
      const nextFilter: GenericFilter = { column: kind, values: [], is_exclude: operator === 'is_not' };
      setLocalFilter(nextFilter);
      onChange(nextFilter);
      setSearchFilterValue('');
      setOpenPopover(openValueOnFieldSelect ? 'value' : null);
    },
    [onChange, openValueOnFieldSelect, operator],
  );

  const handleSelectFilterValue = useCallback(
    (value: AvailableValue, checked: CheckedState) => {
      const baseValues = hasSelectedAll ? allValuesSnapshot : targetFilterValues;
      const valuesSet = new Set(baseValues);

      if (checked) {
        valuesSet.add(value.value);
      } else {
        valuesSet.delete(value.value);
      }

      commitFilterValues(Array.from(valuesSet), hasSelectedAll ? false : undefined);
    },
    [allValuesSnapshot, commitFilterValues, hasSelectedAll, targetFilterValues],
  );

  const handleChangeSortByDirection = useCallback(
    (by: QueryOrdering['by']) => {
      const newDirection = sortBy.direction === 'asc' ? 'desc' : 'asc';
      setSortBy({ by, direction: newDirection });
    },
    [sortBy],
  );

  const handleChangeSortBy = (v: QueryOrdering['by']) => {
    const isAlpha = v === 'alphabetical';
    const dir = isAlpha ? 'asc' : 'desc';
    setSortBy({ by: v, direction: dir });
  };

  const fieldDisplay = filterKind ? buildFilterKindName(filterKind, aliasKindName) : undefined;
  const operatorDisplay = getOperatorLabel(operator);
  const valueDisplay =
    filterKind && targetFilterValues.length > 0
      ? formatValuesForPillDisplay(
          filterKind,
          targetFilterValues.map((value) =>
            translate(filterKind as keyof typeof translate, value) === value
              ? humanizeString(value)
              : translate(filterKind as keyof typeof translate, value),
          ),
        )
      : localFilter?.select_all
        ? 'ALL'
        : undefined;

  const isValueDisabled = !filterKind;
  const isValuePlaceholder = !valueDisplay;

  return (
    <div className={cn('flex w-full items-center gap-2', className)}>
      <Popover
        modal={false}
        open={openPopover === 'field'}
        onOpenChange={(open) => setOpenPopover(open ? 'field' : null)}
      >
        <PopoverTrigger asChild>
          <div>
            <FilterContentV2Pill
              label={fieldDisplay ?? 'Select'}
              placeholder="Select"
              isPlaceholder={!filterKind}
              className="h-8 min-w-24"
              classNames={{ label: PILL_LABEL_CLASS_NAMES, chevron: PILL_CHEVRON_CLASS_NAMES }}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className={cn(NESTED_POPOVER_CONTENT_CLASS, 'w-auto border-0 bg-transparent p-0 shadow-none')}
          align="start"
        >
          <div className="max-h-60 w-56 overflow-hidden rounded-md border border-zinc-800 bg-[#09090B] shadow-md">
            <List className="scrollbar max-h-60 min-w-0 overflow-y-auto p-1">
              {fieldKinds.map((kind) => (
                <ListItem
                  key={kind}
                  className={cn(
                    'cursor-pointer rounded-[8px] p-2 text-xs text-white hover:bg-neutral-800',
                    filterKind === kind && 'bg-neutral-800',
                  )}
                  onClick={() => handleSelectField(kind)}
                >
                  {buildFilterKindName(kind, aliasKindName)}
                </ListItem>
              ))}
            </List>
          </div>
        </PopoverContent>
      </Popover>

      <FilterContentV2Pill
        label={operatorDisplay}
        disabled
        hideChevron
        className="h-8 w-14 shrink-0 cursor-default"
        classNames={{ label: PILL_LABEL_CLASS_NAMES }}
      />

      <Popover
        modal={false}
        open={openPopover === 'value'}
        onOpenChange={(open) => {
          if (!open) setOpenPopover(null);
        }}
      >
        <PopoverTrigger asChild disabled={isValueDisabled}>
          <div className={cn('min-w-0 flex-1', isValueDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
            <FilterContentV2Pill
              data-filter-value-trigger
              className="h-8 w-full min-w-25"
              label={valueDisplay ?? 'Select value'}
              placeholder="Select value"
              isPlaceholder={isValuePlaceholder}
              disabled={isValueDisabled}
              highlight={!isValueDisabled && (openPopover === 'value' || isValuePlaceholder)}
              hideChevron
              classNames={{ label: PILL_LABEL_CLASS_NAMES }}
              onClick={(event) => {
                if (isValueDisabled) return;
                event?.preventDefault();
                if (openPopover === 'value') {
                  setOpenPopover(null);
                  return;
                }
                setOpenPopover('value');
              }}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className={cn(NESTED_POPOVER_CONTENT_CLASS, 'w-auto border-0 bg-transparent p-0 shadow-none')}
          align="start"
        >
          {filterKind && (
            <FilterContentV2ValueDropdown
              filterKind={filterKind}
              operator={operator}
              containsText=""
              onContainsTextChange={() => {}}
              onApplyContains={() => setOpenPopover(null)}
              searchFilterValue={searchFilterValue}
              setSearchFilterValue={setSearchFilterValue}
              sortBy={sortBy}
              onChangeSortBy={handleChangeSortBy}
              onChangeSortByDirection={handleChangeSortByDirection}
              availableFilterValues={tempAvailableFilterValues}
              isLoading={isLoading}
              targetFilterValues={targetFilterValues}
              hasSelectedAll={hasSelectedAll}
              showSelectAll={showSelectAll}
              onCheckSelectAll={handleOnCheckSelectAll}
              onSelectFilterValue={handleSelectFilterValue}
              volumeKind={volumeKind}
            />
          )}
        </PopoverContent>
      </Popover>

      {showRemove && onRemove && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0 rounded-base border-zinc-800 bg-[#09090B] text-white opacity-80"
          onClick={onRemove}
          aria-label="Remove nested filter"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
